
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {FilledOrdersSummaryInfoBySymbol} from './FilledOrdersSummaryInfoBySymbol';
import {FieldModelFilledOrdersSummaryInfoBySymbol} from './FilledOrdersSummaryInfoBySymbol';

/**
 * FilledOrdersSummaryInfo struct
 */
class FilledOrdersSummaryInfo {
  /**
   * Initialize struct
   * @param {!Array=} symbolsInfo
   * @param {!FilledOrdersSummaryInfoBySymbol=} total
   * @constructor
   */
  constructor (symbolsInfo = [], total = new FilledOrdersSummaryInfoBySymbol()) {
    this.SymbolsInfo = symbolsInfo
    this.Total = total
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FilledOrdersSummaryInfo}
   * @param {!FilledOrdersSummaryInfo} other Other struct
   * @returns {!FilledOrdersSummaryInfo} This struct
   */
  copy (other) {
    if (other.SymbolsInfo != null) {
      this.SymbolsInfo = []
      for (let item of other.SymbolsInfo) {
        if (item != null) {
          let tempItem
          tempItem = FilledOrdersSummaryInfoBySymbol.fromObject(item)
          this.SymbolsInfo.push(tempItem)
        } else {
          this.SymbolsInfo.push(null)
        }
      }
    } else {
      this.SymbolsInfo = null
    }
    if (other.Total != null) {
      this.Total = FilledOrdersSummaryInfoBySymbol.fromObject(other.Total)
    } else {
      this.Total = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FilledOrdersSummaryInfo}
   * @returns {!FilledOrdersSummaryInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FilledOrdersSummaryInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FilledOrdersSummaryInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FilledOrdersSummaryInfo}
   * @param {!FilledOrdersSummaryInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FilledOrdersSummaryInfo)) {
      throw new TypeError('Instance of FilledOrdersSummaryInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FilledOrdersSummaryInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SymbolsInfo: ((this.SymbolsInfo != null) ? Array.from(this.SymbolsInfo, item => ((item != null) ? item : null)) : null),
      Total: ((this.Total != null) ? this.Total : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FilledOrdersSummaryInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FilledOrdersSummaryInfo} other Object value
   * @returns {!FilledOrdersSummaryInfo} Created struct
   */
  static fromObject (other) {
    return new FilledOrdersSummaryInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FilledOrdersSummaryInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FilledOrdersSummaryInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FilledOrdersSummaryInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6438
  }
}

export { FilledOrdersSummaryInfo };

/**
 * Fast Binary Encoding FilledOrdersSummaryInfo field model
 */
class FieldModelFilledOrdersSummaryInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SymbolsInfo = new fbe.FieldModelVector(new FieldModelFilledOrdersSummaryInfoBySymbol(buffer, 4 + 4), buffer, 4 + 4)
    this.Total = new FieldModelFilledOrdersSummaryInfoBySymbol(buffer, this.SymbolsInfo.fbeOffset + this.SymbolsInfo.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SymbolsInfo.fbeSize + this.Total.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SymbolsInfo.fbeExtra + this.Total.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFilledOrdersSummaryInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6438
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolsInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolsInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolsInfo.fbeSize

    if ((fbeCurrentSize + this.Total.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Total.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Total.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {!FilledOrdersSummaryInfo} fbeValue Default value, defaults is new FilledOrdersSummaryInfo()
   * @returns {!FilledOrdersSummaryInfo} FilledOrdersSummaryInfo value
   */
  get (fbeValue = new FilledOrdersSummaryInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {!FilledOrdersSummaryInfo} fbeValue FilledOrdersSummaryInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolsInfo.fbeSize) <= fbeStructSize) {
      this.SymbolsInfo.get(fbeValue.SymbolsInfo)
    } else {
      fbeValue.SymbolsInfo.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolsInfo.fbeSize

    if ((fbeCurrentSize + this.Total.fbeSize) <= fbeStructSize) {
      fbeValue.Total = this.Total.get()
    } else {
      fbeValue.Total = new FilledOrdersSummaryInfoBySymbol()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Total.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {!FilledOrdersSummaryInfo} fbeValue FilledOrdersSummaryInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFilledOrdersSummaryInfo}
   * @param {FilledOrdersSummaryInfo} fbeValue FilledOrdersSummaryInfo value
   */
  setFields (fbeValue) {
    this.SymbolsInfo.set(fbeValue.SymbolsInfo)
    this.Total.set(fbeValue.Total)
  }
}

export { FieldModelFilledOrdersSummaryInfo };

/**
 * Fast Binary Encoding FilledOrdersSummaryInfo model
 */
class FilledOrdersSummaryInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFilledOrdersSummaryInfo(this.buffer, 4)
  }

  /**
   * Get the FilledOrdersSummaryInfo model
   * @this {!FilledOrdersSummaryInfoModel}
   * @returns {!FieldModelFilledOrdersSummaryInfo} model FilledOrdersSummaryInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FilledOrdersSummaryInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FilledOrdersSummaryInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FilledOrdersSummaryInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FilledOrdersSummaryInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFilledOrdersSummaryInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FilledOrdersSummaryInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FilledOrdersSummaryInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FilledOrdersSummaryInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FilledOrdersSummaryInfoModel}
   * @param {!FilledOrdersSummaryInfo} value FilledOrdersSummaryInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FilledOrdersSummaryInfoModel}
   * @param {!FilledOrdersSummaryInfo} value FilledOrdersSummaryInfo value, defaults is new FilledOrdersSummaryInfo()
   * @return {!object} Deserialized FilledOrdersSummaryInfo value and its size
   */
  deserialize (value = new FilledOrdersSummaryInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FilledOrdersSummaryInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FilledOrdersSummaryInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FilledOrdersSummaryInfoModel}
   * @param {!number} prev Previous FilledOrdersSummaryInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FilledOrdersSummaryInfoModel };
